import { unzip, Unzipped, UnzipFileFilter } from 'fflate'
import { CreativeImportValidationReport } from '@marketing-milk/interfaces'

// The report's image paths are normalized against the archive root after the
// server strips a single shared wrapper folder; the raw ZIP entries may still
// carry that wrapper. Mirror the server's rule exactly: strip the first path
// segment only when every kept entry lives under the same one — never strip
// per-entry, or an unrelated nested file (junk/images/a.png) could match a
// manifest path (images/a.png) in a mixed-root archive.

const JUNK_PREFIX = '__MACOSX/'

function isJunkEntry(name: string): boolean {
  if (name.endsWith('/')) return true // directory entry
  if (name.startsWith(JUNK_PREFIX)) return true
  const base = name.split('/').pop() ?? ''
  return base.startsWith('.')
}

/** Strip exactly one shared top-level folder when every file lives under it (mirrors the nest zip reader). */
function stripWrapperFolder(paths: string[]): (p: string) => string {
  const firstSegments = new Set(paths.map(p => p.split('/')[0]))
  if (paths.length === 0 || firstSegments.size !== 1) return p => p
  const wrapper = `${firstSegments.values().next().value}/`
  if (!paths.every(p => p.startsWith(wrapper))) return p => p
  return p => p.slice(wrapper.length)
}

// fflate's async API inflates on worker threads, keeping big archives off the
// UI thread (unzipSync could block the tab for the duration of a 500 MB batch).
function unzipAsync(buffer: Uint8Array, filter: UnzipFileFilter): Promise<Unzipped> {
  return new Promise((resolve, reject) =>
    unzip(buffer, { filter }, (err, data) => (err ? reject(err) : resolve(data)))
  )
}

function mimeFor(path: string): string {
  return path.toLowerCase().endsWith('.png') ? 'image/png' : 'image/jpeg'
}

/**
 * Extracts thumbnails for report-referenced images straight from the uploaded
 * ZIP in the browser. Returns object URLs keyed by rowNumber — the caller owns
 * revoking them.
 */
export async function extractRowThumbnails(
  file: File,
  report: CreativeImportValidationReport
): Promise<Record<number, string>> {
  const wantedPaths = new Set(
    report.rows.map(row => row.row.image_path).filter((path): path is string => Boolean(path))
  )
  if (!wantedPaths.size) return {}

  const buffer = new Uint8Array(await file.arrayBuffer())

  // Pass 1: walk entry names only (filter always declines, so nothing
  // inflates) to compute the same wrapper normalization the server applied.
  const entryNames: string[] = []
  await unzipAsync(buffer, entry => {
    entryNames.push(entry.name)
    return false
  })
  const normalize = stripWrapperFolder(entryNames.filter(name => !isJunkEntry(name)))

  // Pass 2: inflate exactly the entries whose normalized path is referenced.
  const entries = await unzipAsync(
    buffer,
    entry => !isJunkEntry(entry.name) && wantedPaths.has(normalize(entry.name))
  )

  const byPath = new Map<string, string>()
  for (const [entryName, data] of Object.entries(entries)) {
    const path = normalize(entryName)
    if (!byPath.has(path)) {
      byPath.set(path, URL.createObjectURL(new Blob([data], { type: mimeFor(path) })))
    }
  }

  return Object.fromEntries(
    report.rows
      .filter(row => row.row.image_path && byPath.has(row.row.image_path))
      .map(row => [row.rowNumber, byPath.get(row.row.image_path)!])
  )
}
